// Asset Types
export type AssetType = 'IMAGE' | 'VIDEO' | 'AUDIO' | 'ZIP' | 'DOCUMENT' | 'OTHER';

export type MetadataFieldType = 'TEXT' | 'TEXTAREA' | 'CHECKBOX' | 'DROPDOWN' | 'TAGS';

export interface MetadataFieldOption {
    value: string;
    label: string;
}

export interface MetadataFieldDef {
    fieldKey: string;
    label: string;
    type: MetadataFieldType;
    required: boolean;
    multiple: boolean;
    options: MetadataFieldOption[];
    helpText?: string;
    order: number;
    defaultValue?: unknown;
}

export interface FolderMetadataSchema {
    inheritFromParent: boolean;
    schemaVersion: number;
    fields: MetadataFieldDef[];
}

export interface EffectiveMetadataSchema {
    fields: MetadataFieldDef[];
    inheritedFromFolderId: string;
    inheritedFromTitle: string | null;
    inheritedFromName: string;
    schemaVersion: number;
    inheritFromParent: boolean;
}

export interface AssetCustomFieldValue {
    fieldKey: string;
    fieldType: MetadataFieldType;
    value?: unknown;
    valueText?: string;
}

export interface AssetMetadata {
    description?: string;
    tags?: string[];
    width?: number;
    height?: number;
    duration?: number;
    customFields?: AssetCustomFieldValue[];
    schemaFolderId?: string;
    schemaVersion?: number;
}

export interface Asset {
    _id: string;
    companyId: string;
    folderId: string;
    name: string;
    originalName: string;
    type: AssetType;
    mimeType: string;
    size: number;
    s3Key: string;
    s3Bucket: string;
    s3Region: string;
    s3Url: string;
    thumbnailS3Key?: string;
    /** CDN-relative path when published, e.g. `/PUBLISHED/DEV/<userId>/AssetExplorer/...` */
    publishedRelativeUrl?: string | null;
    uploadedBy: string;
    uploadedByEmail?: string;
    metadata?: AssetMetadata;
    createdAt: string;
    updatedAt: string;
}

// Folder Types
export interface FolderMetadata {
    description?: string;
    tags?: string[];
}

export interface Folder {
    _id: string;
    companyId: string;
    /** Explorer scope: owning user (Mongo ObjectId string); draft tree is per user per environment segment. */
    ownerUserId?: string;
    /** DEV | STAGE | PROD — derived from server ENV_TYPE. */
    envSegment?: string;
    name: string;
    /** Display title; falls back to `name`. Folder name is immutable. */
    title?: string;
    parentId: string | null;
    path: string;
    s3Path: string;
    createdBy: string;
    updatedBy: string;
    isRoot: boolean;
    /** True after the folder itself is published. Survives unpublished assets. */
    published?: boolean;
    /** Owner has shared this published folder with the company. */
    sharedWithCompany?: boolean;
    /** This copy was shared by another user. It is read-only. */
    shared?: boolean;
    hasThumbnail?: boolean;
    thumbnailContentType?: string | null;
    thumbnailUpdatedAt?: string | null;
    metadata?: FolderMetadata;
    metadataSchema?: FolderMetadataSchema;
    metadataUpdatedAt?: string | null;
    createdAt: string;
    updatedAt: string;
    children?: Folder[];
    assetCount?: number;
    subfolderCount?: number;
}

// API Response Types
export interface PaginationInfo {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

/** Company-wide DAM storage vs the active assetsmanagement.ai plan quota. */
export interface StorageUsage {
    usedBytes: number;
    usedGb: number;
    totalSizeInPlan: number | null;
    percentUsed: number | null;
}

export interface AssetsResponse {
    assets: Asset[];
    pagination: PaginationInfo;
}

export interface FolderTreeResponse extends Folder {
    children: FolderTreeResponse[];
}

// Upload Types
export interface UploadAssetPayload {
    folderId: string;
    name: string;
    type: AssetType;
    assetData: string; // Base64 encoded
    mimeType: string;
    originalName: string;
    size: number;
    metadata?: AssetMetadata;
}

export interface CreateFolderPayload {
    name: string;
    parentId?: string;
}

// View Types
export type ViewMode = 'grid' | 'table';

// Sort Types
export type SortField = 'name' | 'createdAt' | 'updatedAt' | 'size' | 'type';
export type SortOrder = 'asc' | 'desc';

export interface SortConfig {
    field: SortField;
    order: SortOrder;
}

// Filter Types
export interface FilterConfig {
    searchQuery?: string;
    type?: AssetType | 'ALL';
}

// Selection Types
export interface SelectionState {
    selectedAssets: Set<string>;
    selectAll: boolean;
}

// Dialog Types
export interface UploadDialogState {
    open: boolean;
    folderId: string;
}

export interface DeleteDialogState {
    open: boolean;
    assetIds: string[];
    assetNames: string[];
    folderIds: string[];
    folderNames: string[];
}

export interface CreateFolderDialogState {
    open: boolean;
    parentId: string | null;
}

export interface FolderPropertiesDialogState {
    open: boolean;
    folderId: string;
}

export interface AssetPropertiesDialogState {
    open: boolean;
    assetIds: string[];
}

export interface FolderPropertiesResponse {
    folderId: string;
    name: string;
    title: string;
    isRoot: boolean;
    parentId: string | null;
    hasThumbnail: boolean;
    thumbnailContentType: string | null;
    thumbnailUpdatedAt: string | null;
    inheritFromParent: boolean;
    fields: MetadataFieldDef[];
    storedFields: MetadataFieldDef[];
    effectiveSchema: EffectiveMetadataSchema;
    metadataUpdatedAt: string | null;
    defaultsAppliedCount?: number;
}

export interface SaveFolderPropertiesPayload {
    title?: string;
    inheritFromParent: boolean;
    fields?: MetadataFieldDef[];
    thumbnailBase64?: string;
    thumbnailContentType?: string;
    removeThumbnail?: boolean;
    applyDefaultsToDescendants?: boolean;
}

export interface AssetPropertiesResponse {
    assetId: string;
    name: string;
    folderId: string;
    effectiveSchema: EffectiveMetadataSchema;
    customFields: AssetCustomFieldValue[];
}

// Component Props Types
export interface DirectoryTreeProps {
    folders: FolderTreeResponse[];
    selectedFolderId: string | null;
    onFolderSelect: (folder: FolderTreeResponse) => void;
    onFolderCreate: (parentId: string | null) => void;
    onFolderDelete: (folderId: string) => void;
    onFolderProperties?: (folderId: string) => void;
    loading?: boolean;
    /** Desktop sidebar: collapses the folders panel to a thin strip */
    onCollapseSidebar?: () => void;
    /** Square top corners on the title banner (e.g. mobile drawer where another header sits above the tree). */
    squareTitleBannerTop?: boolean;
    /** Minimal tree: no banner/toolbar/context menus (e.g. move destination picker). */
    pickerMode?: boolean;
    /** Grey out and block selection (folder ids). */
    disabledFolderIds?: Set<string>;
}

export interface AssetGridProps {
    /** Subfolders of the current folder (search/sort applied by parent). Shown before assets. */
    childFolders?: FolderTreeResponse[];
    onFolderOpen?: (folder: FolderTreeResponse) => void;
    selectedFolderIds?: Set<string>;
    onFolderToggleSelect?: (folderId: string) => void;
    assets: Asset[];
    selectedAssets: Set<string>;
    onAssetSelect: (assetId: string) => void;
    /** Opens fullscreen carousel when user clicks the thumbnail / hero area */
    onThumbnailClick: (asset: Asset) => void;
    onAssetDownload: (asset: Asset) => void;
    onAssetDelete: (asset: Asset) => void;
    loading?: boolean;
    /** Shared company folders are view and download only. */
    readOnly?: boolean;
}

export interface AssetCarouselDialogProps {
    open: boolean;
    onClose: () => void;
    assets: Asset[];
    initialIndex: number;
    onDownload: (asset: Asset) => void;
}

export interface AssetTableProps {
    childFolders?: FolderTreeResponse[];
    onFolderOpen?: (folder: FolderTreeResponse) => void;
    selectedFolderIds?: Set<string>;
    onFolderToggleSelect?: (folderId: string) => void;
    assets: Asset[];
    selectedAssets: Set<string>;
    onAssetSelect: (assetId: string) => void;
    /** Replace selection with these folder/asset ids (from rows visible in the table, incl. column filters). */
    onSelectAllDisplayed: (folderIds: string[], assetIds: string[]) => void;
    sortConfig: SortConfig;
    onSortChange: (field: SortField) => void;
    filterConfig: FilterConfig;
    onFilterChange: (filter: FilterConfig) => void;
    loading?: boolean;
}

export interface UploadDialogProps {
    open: boolean;
    folderId: string;
    onClose: () => void;
    onUploadComplete: () => void;
}

export interface DeleteConfirmDialogProps {
    open: boolean;
    assetCount: number;
    assetNames: string[];
    folderCount?: number;
    folderNames?: string[];
    onConfirm: () => void;
    onCancel: () => void;
    loading?: boolean;
}

export interface MoveDestinationDialogProps {
    open: boolean;
    onClose: () => void;
    folders: FolderTreeResponse[];
    folderTreeLoading: boolean;
    invalidDestinationIds: Set<string>;
    onConfirm: (destinationFolderId: string) => void | Promise<void>;
    confirmLoading: boolean;
}

export interface CreateFolderDialogProps {
    open: boolean;
    parentId: string | null;
    onClose: () => void;
    onCreateComplete: () => void;
}

export interface FolderPropertiesDialogProps {
    open: boolean;
    folderId: string;
    onClose: () => void;
    onSaved: (result?: { defaultsAppliedCount?: number }) => void;
}

export interface AssetPropertiesDialogProps {
    open: boolean;
    assetIds: string[];
    onClose: () => void;
    onSaved: () => void;
}

export const folderDisplayName = (folder: { name: string; title?: string | null; isRoot?: boolean; shared?: boolean; parentId?: string | null }): string => {
    if (folder.isRoot) return 'My Assets';
    const label = (folder.title && folder.title.trim()) || folder.name;
    return folder.shared && !folder.parentId ? `${label} (shared)` : label;
};
